import React, { useState, useEffect } from 'react';
import api, { BACKEND_URL } from '../api/axios';
import AgentLayout from './AgentLayout';

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-600',
  paid: 'bg-green-50 text-green-600',
};

function AgentCommissions() {
  const [commissions, setCommissions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ candidate_id: '', amount: '', status: 'pending' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [updatingId, setUpdatingId] = useState(null);

  const loadCommissions = async (status = statusFilter) => {
    setLoading(true);
    setError('');
    try {
      const params = status ? { status } : {};
      const res = await api.get('/agent/commissions', { params });
      setCommissions(res.data);
    } catch (err) {
      console.error('Failed to load commissions', err);
      setError('Could not load commissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCommissions('');
    (async () => {
      try {
        const res = await api.get('/agent/candidates');
        setCandidates(res.data);
      } catch (err) {
        console.error('Failed to load candidates', err);
      }
    })();
  }, []);

  const handleFilterChange = (status) => {
    setStatusFilter(status);
    loadCommissions(status);
  };

  const openForm = () => {
    setForm({ candidate_id: '', amount: '', status: 'pending' });
    setFormError('');
    setShowForm(true);
  };

  const saveCommission = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      setFormError('Enter a valid amount.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await api.post('/agent/commissions', {
        candidate_id: form.candidate_id || null,
        amount: form.amount,
        status: form.status,
      });
      setShowForm(false);
      loadCommissions();
    } catch (err) {
      const data = err.response?.data;
      setFormError(data?.message || 'Could not save commission.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (c) => {
    const newStatus = c.status === 'pending' ? 'paid' : 'pending';
    setUpdatingId(c.id);
    try {
      await api.put(`/agent/commissions/${c.id}/status`, { status: newStatus });
      loadCommissions();
    } catch (err) {
      console.error('Failed to update status', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const totalPending = commissions
    .filter((c) => c.status === 'pending')
    .reduce((sum, c) => sum + Number(c.amount), 0);
  const totalPaid = commissions
    .filter((c) => c.status === 'paid')
    .reduce((sum, c) => sum + Number(c.amount), 0);

  return (
    <AgentLayout>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-2xl font-bold text-[#0D1B3D]">Commissions</h2>
        <button
          onClick={openForm}
          className="bg-[#0D1B3D] text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          + Add Commission
        </button>
      </div>
      <p className="text-gray-500 text-sm mb-6">Track your commission entries and payment status.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-xs text-gray-400 mb-1">Pending</p>
          <p className="text-2xl font-bold text-amber-600">{fmtMoney(totalPending)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-xs text-gray-400 mb-1">Paid</p>
          <p className="text-2xl font-bold text-green-600">{fmtMoney(totalPaid)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hidden md:block">
          <p className="text-xs text-gray-400 mb-1">Total</p>
          <p className="text-2xl font-bold text-[#0D1B3D]">{fmtMoney(totalPending + totalPaid)}</p>
        </div>
      </div>

      {showForm && (
        <div className="bg-blue-50/40 rounded-xl border border-gray-100 p-5 mb-6">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Candidate (optional)</label>
              <select
                value={form.candidate_id}
                onChange={(e) => setForm({ ...form, candidate_id: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[200px]"
              >
                <option value="">— None —</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <button
              onClick={saveCommission}
              disabled={saving}
              className="bg-[#0D1B3D] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="text-gray-500 text-sm hover:underline"
            >
              Cancel
            </button>
          </div>
          {formError && <p className="text-red-500 text-xs mt-2">{formError}</p>}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6 flex gap-2">
        {['', 'pending', 'paid'].map((s) => (
          <button
            key={s || 'all'}
            onClick={() => handleFilterChange(s)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full ${
              statusFilter === s ? 'bg-[#0D1B3D] text-white' : 'bg-gray-50 text-gray-500'
            }`}
          >
            {s === '' ? 'All' : s === 'pending' ? 'Pending' : 'Paid'}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3 mb-4">{error}</div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 text-xs uppercase border-b border-gray-100">
              <th className="px-5 py-3 font-medium">Candidate</th>
              <th className="px-5 py-3 font-medium">Amount</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Created</th>
              <th className="px-5 py-3 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="py-6 text-center text-gray-400">Loading commissions…</td></tr>
            )}
            {!loading && commissions.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-gray-400">No commission entries yet.</td></tr>
            )}
            {!loading && commissions.map((c) => (
              <tr key={c.id} className="border-t border-gray-50">
                <td className="px-5 py-3">{c.candidate?.name || '—'}</td>
                <td className="px-5 py-3 font-medium">{fmtMoney(c.amount)}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${STATUS_STYLES[c.status] || 'bg-gray-50 text-gray-500'}`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-5 py-3">{fmtDateTime(c.created_at)}</td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => toggleStatus(c)}
                    disabled={updatingId === c.id}
                    className="text-blue-600 hover:underline text-xs disabled:opacity-50"
                  >
                    {updatingId === c.id
                      ? 'Updating...'
                      : c.status === 'pending' ? 'Mark Paid' : 'Mark Pending'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AgentLayout>
  );
}

export default AgentCommissions;